/**
 * Browser twin of the New Patient form rules (KTD3): which fields show and
 * which are required in a mode, the one emptiness rule per input kind, the
 * missing-required list, and visible-default seeding.
 *
 * Pure and component-free so tests can import it (page modules cannot be
 * imported by a test; see the TasksPage/taskPayload precedent).
 *
 * Server twins:
 *  - NewPatientFormResolver (api/app/Services/NewPatientForm) resolves the
 *    fields this module reads; isShown/isRequired re-apply its forcing rules
 *    defensively so a stale or hand-edited row can't unlock a core field.
 *  - FieldEmptiness is the PHP twin of isEmpty. Both are tested against the
 *    shared cases in __fixtures__/catalog.json.
 *  - IntakeFieldRules::FIELD_MAP / isFilled is the twin of fieldValue.
 */

export type FormMode = 'new' | 'convert_lead' | 'add_plan';

export type FieldCard = 'patient' | 'contact' | 'plan' | 'interests' | 'notes';

export type FieldKind =
  | 'text'
  | 'name'
  | 'date'
  | 'select'
  | 'rating'
  | 'yesno'
  | 'checkbox'
  | 'number'
  | 'height'
  | 'procedure'
  | 'file';

/** Cards in their fixed render order (R3). */
export const CARD_ORDER: readonly FieldCard[] = ['patient', 'contact', 'plan', 'interests', 'notes'];

export interface FieldModeState {
  shown: boolean;
  required: boolean;
}

/** One field as the staff GET /new-patient-form returns it. */
export interface ResolvedField {
  key: string;
  card: FieldCard;
  order: number;
  /** customLabel when set, else the catalog caption. */
  label: string;
  customLabel: string | null;
  caption: string;
  hint: string | null;
  default: string | null;
  available: boolean;
  locked: boolean;
  kind: FieldKind;
  acceptsDefault: boolean;
  hasRequired: boolean;
  allowedModes: FormMode[];
  modes: Record<FormMode, FieldModeState>;
}

export interface NewPatientFormSettings {
  hintsEnabled: boolean;
  fields: ResolvedField[];
  /** list => { "<option id>": hint } */
  optionHints: Partial<Record<OptionHintList, Record<string, string>>>;
}

export type OptionHintList =
  | 'doctors'
  | 'facilities'
  | 'languages'
  | 'procedures_subcategories'
  | 'referral_level1'
  | 'referral_level2';

/** The referring patient as the form holds it (a PatientSummary subset). */
export interface ReferralPatientRef {
  patientId: number;
  firstName: string;
  lastName: string;
}

/**
 * The New Patient form's values, one slot per NewLeadPage useState field that
 * feeds the save. Types match those useState declarations so the page can
 * assemble this object from its state without conversion.
 */
export interface FormState {
  // Patient Information
  firstName: string;
  middleName: string;
  lastName: string;
  maidenName: string;
  gender: string | null;
  birthDate: string;
  /** Whether an ID scan was captured on this form (the scan uploads separately). */
  idScanCaptured: boolean;
  // Contact Information
  cellPhone: string;
  /** The cell's dial code ('44'), never a countryID. */
  cellCountryCode: string;
  smsOptIn: boolean;
  marketingSms: boolean;
  email: string;
  emailOptIn: boolean;
  marketingEmails: boolean;
  address: string;
  city: string;
  state: string;
  zip: string;
  /** Country code ('US' / 'CA'). */
  addressCountry: string;
  // Plan Setup
  referralCategoryId: number | null;
  referralSourceId: number | null;
  referralPatient: ReferralPatientRef | null;
  doctorId: number | null;
  coordinatorId: number | null;
  nurseId: number | null;
  facilityId: number | null;
  planTypeId: number | null;
  // Interests
  timeframe: number | null;
  commitmentLevel: number | null;
  revenueOpportunity: string;
  // Notes
  notes: string;
}

/** NewLeadPage's initial state (facility unset). */
export function emptyFormState(): FormState {
  return {
    firstName: '',
    middleName: '',
    lastName: '',
    maidenName: '',
    gender: null,
    birthDate: '',
    idScanCaptured: false,
    cellPhone: '',
    cellCountryCode: '1',
    smsOptIn: true,
    marketingSms: true,
    email: '',
    emailOptIn: true,
    marketingEmails: true,
    address: '',
    city: '',
    state: '',
    zip: '',
    addressCountry: 'US',
    referralCategoryId: null,
    referralSourceId: null,
    referralPatient: null,
    doctorId: null,
    coordinatorId: null,
    nurseId: null,
    facilityId: null,
    planTypeId: null,
    timeframe: null,
    commitmentLevel: null,
    revenueOpportunity: '',
    notes: '',
  };
}

// ---------------------------------------------------------------------------
// Visibility and required
// ---------------------------------------------------------------------------

/**
 * Shown in this mode: available, offered in the mode, and either locked
 * (forced shown, R5) or switched on.
 */
export function isShown(field: ResolvedField, mode: FormMode): boolean {
  if (!field.available || field.allowedModes.indexOf(mode) === -1) return false;
  return field.locked || Boolean(field.modes[mode]?.shown);
}

/** Required in this mode. A hidden field is never required (R6). */
export function isRequired(field: ResolvedField, mode: FormMode): boolean {
  if (!isShown(field, mode) || !field.hasRequired) return false;
  return field.locked || Boolean(field.modes[mode]?.required);
}

// ---------------------------------------------------------------------------
// Emptiness (twin of FieldEmptiness.php)
// ---------------------------------------------------------------------------

// PHP trim() strips exactly these; String.prototype.trim strips more (NBSP and
// other Unicode spaces), which would let the two sides disagree.
const PHP_TRIM = /^[ \t\n\r\0\x0B]+|[ \t\n\r\0\x0B]+$/g;
const phpTrim = (value: string) => value.replace(PHP_TRIM, '');

const NUMERIC = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** PHP is_array(): a list or a key/value map. */
function isPhpArray(value: unknown): value is Record<string, unknown> {
  return Array.isArray(value) || isPlainObject(value);
}

function blank(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return phpTrim(value) === '';
  return !(typeof value === 'number' || typeof value === 'boolean');
}

function isNumeric(value: unknown): boolean {
  if (typeof value === 'number') return !Number.isNaN(value);
  if (typeof value === 'string') return NUMERIC.test(phpTrim(value));
  return false;
}

function trimmed(value: unknown): unknown {
  return typeof value === 'string' ? phpTrim(value) : value;
}

function emptySelect(value: unknown): boolean {
  if (blank(value) || typeof value === 'boolean') return true;
  const v = trimmed(value);
  return isNumeric(v) ? Number(v) <= 0 : false;
}

function positive(value: unknown): boolean {
  if (blank(value) || typeof value === 'boolean') return false;
  const v = trimmed(value);
  return isNumeric(v) && Number(v) > 0;
}

function yesNo(value: unknown): boolean {
  if (typeof value === 'boolean') return true;
  const v = trimmed(value);
  return v === 0 || v === 1 || v === '0' || v === '1';
}

function emptyFile(value: unknown): boolean {
  if (value === null || value === undefined || value === false || value === 0 || value === '0') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (isPlainObject(value)) return Object.keys(value).length === 0;
  return typeof value === 'string' && phpTrim(value) === '';
}

/** Whether a value counts as missing for its input kind (KTD3). */
export function isEmpty(kind: FieldKind, value: unknown): boolean {
  switch (kind) {
    case 'text':
      return blank(value);
    case 'name':
      return !isPhpArray(value) || blank(value.first) || blank(value.last);
    case 'date': {
      if (blank(value)) return true;
      const v = phpTrim(String(value));
      return v === '0000-00-00' || v === '0000-00-00 00:00:00';
    }
    case 'select':
      return emptySelect(value);
    case 'rating':
      return !positive(value);
    case 'yesno':
    case 'checkbox':
      return !yesNo(value);
    case 'number':
      return blank(value) || !isNumeric(trimmed(value));
    case 'height':
      return !isPhpArray(value) || !positive(value.feet);
    case 'procedure':
      return !isPhpArray(value) || !positive(value.categoryId) || !positive(value.procedureId);
    case 'file':
      return emptyFile(value);
    default:
      throw new Error(`Unknown New Patient form input kind: ${String(kind)}`);
  }
}

// ---------------------------------------------------------------------------
// Field values (twin of IntakeFieldRules::isFilled)
// ---------------------------------------------------------------------------

/**
 * The value a field holds in form state, shaped for isEmpty. Undefined for a
 * key the form has no input for yet (the Phase B keys).
 */
export function fieldValue(key: string, state: FormState): unknown {
  switch (key) {
    case 'first_name':
      return { first: state.firstName, last: state.lastName };
    case 'middle_name':
      return state.middleName;
    case 'maiden_name':
      return state.maidenName;
    case 'gender':
      return state.gender;
    case 'birth_date':
      return state.birthDate;
    case 'id_scan':
      return state.idScanCaptured;
    case 'cell_phone':
      return state.cellPhone;
    case 'email':
      return state.email;
    case 'address':
      return state.address;
    case 'city':
      return state.city;
    case 'state':
      return state.state;
    case 'zip':
      return state.zip;
    case 'country':
      return state.addressCountry;
    case 'plan_type':
      return state.planTypeId;
    case 'facility':
      return state.facilityId;
    case 'referral_select_1_anchor':
      return state.referralCategoryId;
    case 'referral_name':
      // Filled by a lead source or, for an established-patient category, the
      // referring patient (the server checks either payload key).
      return state.referralSourceId ?? state.referralPatient?.patientId ?? null;
    case 'doctor':
      return state.doctorId;
    case 'coordinator':
      return state.coordinatorId;
    case 'nurse':
      return state.nurseId;
    case 'ball_park_given':
      return state.revenueOpportunity;
    case 'ball_park':
      return state.commitmentLevel;
    case 'timeframe':
      return state.timeframe;
    case 'notes':
      return state.notes;
    default:
      return undefined;
  }
}

/** Whether a field holds a value in form state. */
export function isFieldFilled(field: ResolvedField, state: FormState): boolean {
  return !isEmpty(field.kind, fieldValue(field.key, state));
}

// ---------------------------------------------------------------------------
// Ordering, labels, missing-required
// ---------------------------------------------------------------------------

/** Fields in render order: card order, then order within the card. */
export function inRenderOrder(fields: readonly ResolvedField[]): ResolvedField[] {
  return fields
    .map((field, index) => ({ field, index }))
    .sort((a, b) => {
      const card = CARD_ORDER.indexOf(a.field.card) - CARD_ORDER.indexOf(b.field.card);
      if (card !== 0) return card;
      if (a.field.order !== b.field.order) return a.field.order - b.field.order;
      return a.index - b.index;
    })
    .map((entry) => entry.field);
}

/**
 * The label the form shows: the custom label, else the caller's caption (the
 * account's role title for doctor, coordinator and nurse), else the label.
 */
export function displayLabel(field: ResolvedField, captions: Record<string, string> = {}): string {
  const custom = field.customLabel?.trim();
  if (custom) return custom;
  const caption = captions[field.key]?.trim();
  if (caption) return caption;
  return field.label;
}

export interface MissingField {
  key: string;
  label: string;
}

/** What the form knows about the chosen lead source category. */
export interface RequiredContext {
  /** The category offers sources to pick, so the Lead Source input renders. */
  referralSourcesOffered: boolean;
  /** The category takes a referring patient, which also fills Lead Source. */
  isEstablishedPatientSource: boolean;
}

/**
 * Lead Source (referral_name) is not on the form when the chosen category has
 * no sources to pick and takes no referring patient, so a required Lead
 * Source cannot block the save there.
 */
function requirementWaived(field: ResolvedField, state: FormState, context: RequiredContext | undefined): boolean {
  return field.key === 'referral_name'
    && context !== undefined
    && state.referralCategoryId != null
    && !context.referralSourcesOffered
    && !context.isEstablishedPatientSource;
}

/** Required fields left empty in this mode, in render order (R11). */
export function missingRequired(
  fields: readonly ResolvedField[],
  mode: FormMode,
  state: FormState,
  captions: Record<string, string> = {},
  context?: RequiredContext,
): MissingField[] {
  return inRenderOrder(fields)
    .filter((field) => isRequired(field, mode) && !isFieldFilled(field, state) && !requirementWaived(field, state, context))
    .map((field) => ({ key: field.key, label: displayLabel(field, captions) }));
}

// ---------------------------------------------------------------------------
// Visible defaults (R8, KTD7)
// ---------------------------------------------------------------------------

function positiveId(raw: string): number | null {
  const n = Number(raw.trim());
  return raw.trim() !== '' && Number.isInteger(n) && n > 0 ? n : null;
}

const ID_DEFAULT_SLOTS: Record<string, keyof FormState> = {
  plan_type: 'planTypeId',
  facility: 'facilityId',
  referral_select_1_anchor: 'referralCategoryId',
  referral_name: 'referralSourceId',
  doctor: 'doctorId',
  coordinator: 'coordinatorId',
  nurse: 'nurseId',
  ball_park: 'commitmentLevel',
  timeframe: 'timeframe',
};

const CODE_DEFAULT_SLOTS: Record<string, keyof FormState> = {
  gender: 'gender',
  state: 'state',
  country: 'addressCountry',
};

/**
 * Seed each visible field's default into an empty value. A value already on
 * the form (typed, or prefilled from the lead or patient) is kept. Hidden
 * fields are left alone: the server applies their defaults (KTD7).
 *
 * The Lead Source default is seeded only when `sourceCategory` places it in
 * the category the form ends up holding (prefilled, or the category default
 * seeded here); a source it does not know is not seeded. A source of another
 * category would read as empty on the form yet still be saved.
 *
 * Returns the same object when nothing changed, so a caller can skip a
 * state update.
 */
export function applyVisibleDefaults(
  state: FormState,
  fields: readonly ResolvedField[],
  mode: FormMode,
  sourceCategory: (sourceId: number) => number | null,
): FormState {
  let next: FormState | null = null;
  for (const field of fields) {
    if (!field.acceptsDefault || field.default === null || !isShown(field, mode)) continue;
    const raw = String(field.default);
    if (raw.trim() === '' || isFieldFilled(field, next ?? state)) continue;

    const idSlot = ID_DEFAULT_SLOTS[field.key];
    const codeSlot = CODE_DEFAULT_SLOTS[field.key];
    let patch: Partial<FormState> | null = null;
    if (idSlot) {
      const id = positiveId(raw);
      if (id !== null) patch = { [idSlot]: id } as Partial<FormState>;
    } else if (codeSlot) {
      patch = { [codeSlot]: raw.trim() } as Partial<FormState>;
    }
    if (patch) next = { ...(next ?? state), ...patch };
  }
  if (next && next.referralSourceId !== state.referralSourceId && next.referralSourceId !== null) {
    const categoryId = sourceCategory(next.referralSourceId);
    if (categoryId === null || categoryId !== next.referralCategoryId) {
      next = { ...next, referralSourceId: state.referralSourceId };
    }
  }
  return next ?? state;
}

/**
 * The message for a date of birth of today or later (ISO dates compare as
 * text), or null. `today` is the viewer's own calendar date, as the date of
 * birth picker uses. An empty or legacy zero date is the required check's job.
 */
export function birthDateError(value: string, today: string): string | null {
  const date = value.trim();
  if (date === '' || date.startsWith('0000')) return null;
  return date >= today ? "Date of birth must be before today. Enter the patient's date of birth." : null;
}

/** The address countries the form offers. */
const FORM_COUNTRIES = ['US', 'CA'];

/**
 * The address country the form starts on: the lead's or patient's own
 * country, else Country's configured default (when Country is shown), else
 * the account's country, else US. With no default configured this is the
 * account country, as before settings existed (R12).
 */
export function startingCountry(args: {
  prefill: string | null;
  countryField: ResolvedField | undefined;
  mode: FormMode;
  accountCountry: string | null | undefined;
}): string {
  const prefill = args.prefill?.trim();
  if (prefill) return prefill;
  const field = args.countryField;
  const configured = field && field.acceptsDefault && isShown(field, args.mode) ? field.default?.trim() : '';
  if (configured) return configured;
  if (args.accountCountry && FORM_COUNTRIES.indexOf(args.accountCountry) !== -1) return args.accountCountry;
  return 'US';
}

// ---------------------------------------------------------------------------
// Mode
// ---------------------------------------------------------------------------

/**
 * The form mode (KTD4). A lead means convert_lead; the patient route (Add
 * Plan) means add_plan; anything else, including a patient picked in the
 * match panel, is new, so the locked patient fields stay enforced.
 */
export function resolveFormMode(input: {
  leadId: number | null | undefined;
  patientRoute: boolean;
}): FormMode {
  if (input.leadId != null && input.leadId > 0) return 'convert_lead';
  if (input.patientRoute) return 'add_plan';
  return 'new';
}
